import type { JobCardDataFragment } from "../../../graphQL/fragments/__generated__/jobCardData.gqlgenerated";

import { useLogFeatureDisplay } from "../../../../homepage/logging/DisplayLog";
import { mapBrandingAttributionToReasons } from "../../../../homepage/util/brandedJobUtil";
import { Box } from "../../../../mads";
import { useBrandingMigrationFlags } from "../BrandingMigrationContext";
import { type JobCardDataWithBrandingEligibility, filterValidBrandingAttribution } from "../types";

import { colors } from "@indeed/ifl-tokens";
import { useEffect } from "react";
import { type ViewStyle, Image, StyleSheet } from "react-native";

export type CompanyLogoProps = {
  job: JobCardDataFragment;
  size?: sizeVariants;
  additionalContainerStyle?: ViewStyle;
};

export type sizeVariants = "sm" | "md" | "lg" | "xl";

function isFeaturedEmployer(
  jobPackagingFeatures?: JobCardDataFragment["jobPackagingFeatures"],
): boolean {
  return !!jobPackagingFeatures?.brandedJobs;
}

export function getCompanyLogoUrl(
  job: JobCardDataFragment,
  isBrandedJobMigrationTestActive = false,
) {
  const brandedJob = job as JobCardDataWithBrandingEligibility;
  if (isBrandedJobMigrationTestActive) {
    return brandedJob.brandingEligibility?.shouldShowBranding
      ? (brandedJob.brandingEligibility.brandingInformation?.localizedAssets?.logo?.url ??
          undefined)
      : undefined;
  }
  return isFeaturedEmployer(job.jobPackagingFeatures)
    ? job.employer?.dossier?.images?.squareLogoUrls?.url256
    : undefined;
}

export function CompanyLogoCore({
  jobKey,
  jobPackagingFeatures,
  companyLogoUrl,
  additionalContainerStyle,
  size = "lg",
  brandingEligibility,
}: {
  jobKey: string;
  jobPackagingFeatures?: JobCardDataFragment["jobPackagingFeatures"];
  companyLogoUrl?: string;
  additionalContainerStyle?: ViewStyle;
  size?: sizeVariants;
  brandingEligibility?: JobCardDataWithBrandingEligibility["brandingEligibility"];
}) {
  const logFeatureDisplay = useLogFeatureDisplay();
  const { isBrandedJobMigrationTestActive } = useBrandingMigrationFlags();
  const brandingReasons = isBrandedJobMigrationTestActive
    ? mapBrandingAttributionToReasons(
        filterValidBrandingAttribution(brandingEligibility?.brandingAttribution),
      )
    : jobPackagingFeatures?.brandingReasons;
  const shouldShowBranding = isBrandedJobMigrationTestActive
    ? !!brandingEligibility?.shouldShowBranding
    : !!jobPackagingFeatures?.brandedJobs;
  const logoUrl = shouldShowBranding ? companyLogoUrl : undefined;

  // Log company logo display and branding features
  // @see https://indeed.sourcegraph.com/r/code.corp.indeed.com/jobsearch/mosaic-provider-jobcards@824480f911bd9ab8a4d2113bcca59a789f2beca4/-/blob/src/components/JobCard/MainContent.tsx?L107
  useEffect(() => {
    logFeatureDisplay({ jobKey, field: "companyLogoYN", value: !!logoUrl });
    logFeatureDisplay({ jobKey, field: "brandedJobYN", value: shouldShowBranding });
    logFeatureDisplay({
      jobKey,
      field: "brandedJobPackageYN",
      value: !!brandingReasons?.includes("PREMIUM_SPONSORSHIP"),
    });
    logFeatureDisplay({
      jobKey,
      field: "brandedJobPaidBrandingYN",
      value: !!brandingReasons?.includes("PAID_BRANDING"),
    });
    logFeatureDisplay({
      jobKey,
      field: "brandedJobFeaturedEmployerYN",
      value: !!brandingReasons?.includes("FEATURED_EMPLOYER"),
    });
  }, [jobKey, logoUrl, logFeatureDisplay, shouldShowBranding, brandingReasons]);

  if (!logoUrl) {
    return null;
  }

  let logoSizeStyle;
  let containerSizeStyle;

  switch (size) {
    case "sm":
      logoSizeStyle = styles.companyLogoSmall;
      containerSizeStyle = styles.companyLogoContainerSmall;
      break;
    case "md":
      logoSizeStyle = styles.companyLogoMedium;
      containerSizeStyle = styles.companyLogoContainerMedium;
      break;
    case "lg":
      logoSizeStyle = styles.companyLogoLarge;
      containerSizeStyle = styles.companyLogoContainerLarge;
      break;
    case "xl":
      logoSizeStyle = styles.companyLogoExtraLarge;
      containerSizeStyle = styles.companyLogoContainerExtraLarge;
      break;
  }

  return (
    <Box sx={[containerSizeStyle, additionalContainerStyle]}>
      {/* TODO: Better implement loading placeholder */}
      <Image
        style={logoSizeStyle}
        source={{ uri: logoUrl }}
        accessible={false}
        testID={`company-logo-image-${jobKey}`}
      />
    </Box>
  );
}

export function CompanyLogo({ job, additionalContainerStyle, size = "lg" }: CompanyLogoProps) {
  const { isBrandedJobMigrationTestActive } = useBrandingMigrationFlags();
  const brandedJob = job as JobCardDataWithBrandingEligibility;
  const companyLogoUrl = getCompanyLogoUrl(job, isBrandedJobMigrationTestActive);
  return (
    <CompanyLogoCore
      jobKey={job.key}
      jobPackagingFeatures={job.jobPackagingFeatures}
      companyLogoUrl={companyLogoUrl}
      additionalContainerStyle={additionalContainerStyle}
      size={size}
      brandingEligibility={brandedJob.brandingEligibility}
    />
  );
}

const styles = StyleSheet.create({
  companyLogoContainerSmall: {
    width: 18,
    height: 18,
    overflow: "hidden",
    padding: 2,
  },
  companyLogoSmall: {
    width: 14,
    height: 14,
  },
  companyLogoContainerMedium: {
    width: 36,
    height: 36,
    overflow: "hidden",
    marginRight: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  companyLogoMedium: {
    width: 24,
    height: 24,
  },
  companyLogoContainerLarge: {
    width: 50,
    height: 50,
    padding: 0,
    marginRight: 8,
    borderWidth: 1,
    borderColor: colors.border.inverse["[base]"],
    borderRadius: 8,
    overflow: "hidden",
  },
  companyLogoLarge: {
    width: 50,
    height: 50,
  },
  companyLogoContainerExtraLarge: {
    width: 60,
    height: 60,
    borderWidth: 1,
    borderColor: colors.border.inverse["[base]"],
    borderRadius: 8,
    overflow: "hidden",
    marginLeft: 16,
    marginRight: 12,
  },
  companyLogoExtraLarge: {
    width: 60,
    height: 60,
  },
});
